import { Suspense } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import { ArticleLayout } from '@/components/editorial';
import { getGuide } from '@/content/guides/registry';
import { getGuideBody, getLoadedGuideBody, hasGuideBody } from '@/content/guides/bodies';
import { getGeneratedDoc } from '@/content/guides/generated';
import BlockRenderer from '@/components/editorial/BlockRenderer';

export default function GuideDetail() {
  const { slug } = useParams<{ slug: string }>();
  const guide = getGuide(slug);

  // Generated guides carry their body as JSON blocks rather than a lazy chunk,
  // so they render synchronously — no Suspense, no hydration boundary.
  const generated = slug ? getGeneratedDoc(slug) : undefined;
  if (guide && generated) {
    return (
      <ArticleLayout guide={guide}>
        <BlockRenderer blocks={generated.blocks} slug={generated.slug} />
      </ArticleLayout>
    );
  }

  if (!guide || !slug || !hasGuideBody(slug)) return <GuideNotFound />;

  // Preloaded (prerender + hydration path): render directly. Introducing a
  // Suspense boundary here would break hydration of the prerendered HTML.
  const Loaded = getLoadedGuideBody(slug);
  if (Loaded) {
    return (
      <ArticleLayout guide={guide}>
        <Loaded />
      </ArticleLayout>
    );
  }

  // Client-side navigation: suspending is fine, nothing is being hydrated.
  const Body = getGuideBody(slug)!;
  return (
    <ArticleLayout guide={guide}>
      <Suspense fallback={<ArticleSkeleton />}>
        <Body />
      </Suspense>
    </ArticleLayout>
  );
}

function ArticleSkeleton() {
  return (
    <div className="animate-pulse space-y-4" aria-hidden="true">
      {[...Array(8)].map((_, i) => (
        <div
          key={i}
          className="h-4 rounded bg-ink-100 dark:bg-white/10"
          style={{ width: `${70 + ((i * 13) % 30)}%` }}
        />
      ))}
    </div>
  );
}

function GuideNotFound() {
  return (
    <div className="mx-auto max-w-article px-4 py-24 text-center sm:px-6">
      {/* A 404 has nothing to index — keep it out of the index explicitly. */}
      <SEO
        title="Guide not found"
        description="This guide does not exist or has been moved."
        noindex
      />
      <h1 className="text-2xl font-bold text-ink-900 dark:text-ink-50">Guide not found</h1>
      <p className="mt-2 text-ink-500 dark:text-ink-400">
        This guide may have been moved or renamed.
      </p>
      <Link
        to="/guides"
        className="mt-6 inline-flex rounded-lg bg-jeok-500 px-4 py-2.5 font-medium text-white transition-colors hover:bg-[#A00D25] dark:bg-jeok-dark"
      >
        Browse all guides
      </Link>
    </div>
  );
}
